import { useEffect, useRef, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'

import {
  downloadApplicationPdf,
  downloadPaymentProof,
  fetchMyApplications,
  uploadPaymentProof,
  type Application,
} from '@/api/applicationsApi'
import { ApiClientError } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'

import styles from './PortalPages.module.css'

/** Vendor home: list applications, download PDF, upload proof of payment. */
export function VendorDashboardPage() {
  const { user, isAuthenticated, isAdmin, logout } = useAuth()
  const [applications, setApplications] = useState<Application[]>([])
  const [error, setError] = useState('')
  const [busyId, setBusyId] = useState<number | null>(null)
  const [uploadTargetId, setUploadTargetId] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  async function reload() {
    const result = await fetchMyApplications()
    setApplications(result.applications)
  }

  useEffect(() => {
    if (!isAuthenticated) return
    void (async () => {
      try {
        await reload()
      } catch (err) {
        setError(err instanceof ApiClientError ? err.message : 'Could not load applications.')
      }
    })()
  }, [isAuthenticated])

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (isAdmin) return <Navigate to="/admin" replace />

  async function onDownload(app: Application) {
    setBusyId(app.id)
    setError('')
    try {
      await downloadApplicationPdf(app)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'PDF download failed.')
    } finally {
      setBusyId(null)
    }
  }

  function openUploadPicker(id: number) {
    setUploadTargetId(id)
    setError('')
    fileInputRef.current?.click()
  }

  async function onFileSelected(fileList: FileList | null) {
    const applicationId = uploadTargetId
    const file = fileList?.[0]
    setUploadTargetId(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (!applicationId || !file) return

    setBusyId(applicationId)
    setError('')
    try {
      await uploadPaymentProof(applicationId, file)
      await reload()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Proof of payment upload failed.')
    } finally {
      setBusyId(null)
    }
  }

  async function onDownloadPop(app: Application) {
    setBusyId(app.id)
    setError('')
    try {
      await downloadPaymentProof(app)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Proof of payment download failed.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.brand}>Oikos Market Day</p>
          <h1 className={styles.title}>Vendor area</h1>
          <p className={styles.lead}>
            Welcome, {user?.full_name} — manage your stall applications, indemnity forms, and proof of payment.
          </p>
        </div>
        <div className={styles.actions}>
          <Link className={styles.primary} to="/vendor/apply">
            New application
          </Link>
          <Link className={styles.secondary} to="/profile">
            Profile
          </Link>
          <button type="button" className={styles.ghost} onClick={logout}>
            Sign out
          </button>
        </div>
      </header>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
        hidden
        onChange={(event) => void onFileSelected(event.target.files)}
      />

      {error ? <p className={styles.error}>{error}</p> : null}

      <div className={styles.list}>
        {applications.length === 0 ? (
          <p className={styles.empty}>No applications yet. Start the 1 August 2026 indemnity form.</p>
        ) : (
          applications.map((app) => (
            <article key={app.id} className={styles.item}>
              <div>
                <h2 className={styles.itemTitle}>{app.market_title}</h2>
                <p className={styles.meta}>
                  Status: <strong>{app.status}</strong>
                  {app.fee_amount != null ? ` · Fee R${app.fee_amount.toFixed(2)}` : null}
                </p>
                <p className={styles.meta}>
                  {app.has_payment_proof ? (
                    <>
                      Proof of payment: <strong>uploaded</strong>
                      {app.payment_proof_original_name
                        ? ` · ${app.payment_proof_original_name}`
                        : null}
                    </>
                  ) : (
                    <>Proof of payment: <strong>not uploaded yet</strong></>
                  )}
                </p>
              </div>
              <div className={styles.actionCol}>
                {app.status === 'approved' ? (
                  <Link className={styles.secondary} to={`/vendor/applications/${app.id}`}>
                    View (locked)
                  </Link>
                ) : (
                  <Link className={styles.secondary} to={`/vendor/applications/${app.id}`}>
                    Edit application
                  </Link>
                )}
                <button
                  type="button"
                  className={styles.secondary}
                  disabled={busyId === app.id}
                  onClick={() => void onDownload(app)}
                >
                  {busyId === app.id ? 'Working…' : 'Download PDF'}
                </button>
                <button
                  type="button"
                  className={styles.primary}
                  disabled={busyId === app.id || app.status === 'approved'}
                  title={
                    app.status === 'approved'
                      ? 'Approved applications are locked — ask an admin to change POP'
                      : undefined
                  }
                  onClick={() => openUploadPicker(app.id)}
                >
                  {app.has_payment_proof ? 'Replace POP' : 'Upload POP'}
                </button>
                {app.has_payment_proof ? (
                  <button
                    type="button"
                    className={styles.ghost}
                    disabled={busyId === app.id}
                    onClick={() => void onDownloadPop(app)}
                  >
                    Download POP
                  </button>
                ) : null}
              </div>
            </article>
          ))
        )}
      </div>
    </section>
  )
}
